/**
 * Keeps the offline engine alive for the whole signed-in session and puts its state where every screen can
 * see it (PROJECT_SPEC.md §10: a persistent, obvious offline indicator with a queued-command count).
 *
 * It also does the unglamorous work that makes offline safe: counts the offline window down, checkpoints the
 * device's monotonic clock so a reload does not lose elapsed time, and sends the queue the moment the server
 * is reachable and the right person is signed in.
 */
import { useQuery } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useEffect, useState, useSyncExternalStore } from "react";
import { endpoints } from "../lib/api.js";
import { shouldAutoSync } from "../lib/offline/auto-sync.js";
import { useReachable } from "../lib/offline/connectivity.js";
import type { EngineState, OfflineEngine } from "../lib/offline/engine.js";
import { getEngine, setSignedInUser } from "../lib/offline/runtime.js";

const TICK_MS = 15_000;

interface Ctx {
  engine: OfflineEngine | null;
  state: EngineState | null;
  online: boolean;
}
export const OfflineContext = createContext<Ctx>({ engine: null, state: null, online: true });
export const useOffline = (): Ctx => useContext(OfflineContext);

export function OfflineProvider({ children }: { children: ReactNode }) {
  const [engine, setEngine] = useState<OfflineEngine | null>(null);
  const online = useReachable();
  const me = useQuery({ queryKey: ["me"], queryFn: () => endpoints.me(), staleTime: 300_000, retry: false });
  setSignedInUser(me.data?.id ?? null);

  useEffect(() => {
    let live = true;
    void getEngine().then((e) => live && setEngine(e));
    return () => {
      live = false;
    };
  }, []);

  const state = useSyncExternalStore(
    (fn) => engine?.subscribe(fn) ?? (() => undefined),
    () => engine?.getState() ?? null,
  );

  // the window counts down, and the carried monotonic clock is saved before the page can disappear
  useEffect(() => {
    if (!engine) return;
    const t = setInterval(() => engine.tick(), TICK_MS);
    return () => clearInterval(t);
  }, [engine]);

  // the page can vanish without warning (tablet sleep, tab discarded): save the clock reading as it goes
  useEffect(() => {
    if (!engine) return;
    const save = () => void engine.checkpoint();
    window.addEventListener("pagehide", save);
    document.addEventListener("visibilitychange", save);
    return () => {
      window.removeEventListener("pagehide", save);
      document.removeEventListener("visibilitychange", save);
    };
  }, [engine]);

  // reconnect (or sign-in) with a device that is open: send what is waiting, then take a fresh anchor
  const signedIn = me.data?.id ?? null;
  const due = shouldAutoSync(state, online, signedIn);
  useEffect(() => {
    if (engine && due) void engine.sync();
  }, [engine, due]);

  return <OfflineContext.Provider value={{ engine, state, online }}>{children}</OfflineContext.Provider>;
}
